import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spin, message, Modal, Image } from "antd";
import dayjs from "dayjs";
import { serverFetch } from "@extensions";
import { useCompany, useUser } from "@redux";
import { recordPassVidOptions } from "../../extensions/utils";
import { InfoCircleOutlined } from "@ant-design/icons";
import CatalogSaleDates from "./catalog-sale-dates";
import "./catalog-sale-groups.css";

// С этого числа свободных мест занятость считается "почти заполнена"
const SLOTS_WARNING_REMAINING = 2;

const CatalogSaleGroups = ({ productId, onSelect, initialGroupRecord = null, initialRecordId = null, initialDate = null, selectedClientIds = [], onBookedChange }) => {
  const company = useCompany();
  const user = useUser();
  const initialDateValue = initialDate || initialGroupRecord?.docDate || null;
  const initialSelectedDate = useMemo(() => initialDateValue ? dayjs(initialDateValue) : null, [initialDateValue]);
  const initialSelectedRecordId = initialRecordId || initialGroupRecord?.id || null;
  const initialGroups = useMemo(() => initialGroupRecord ? [initialGroupRecord] : [], [initialGroupRecord]);
  const [groups, setGroups] = useState(initialGroups);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const [selectedRecordId, setSelectedRecordId] = useState(initialSelectedRecordId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalUser, setModalUser] = useState(null);
  // Мои участники, уже занявшие место в показанных занятиях:
  // { id записи: [id клиента] }. Заполняется по составу занятий сразу,
  // поэтому занятые занятия видно ещё до выбора
  const [bookedByRecord, setBookedByRecord] = useState({});

  // Занятые места своих участников считаем по показанным занятиям одним
  // запросом. Сессия уходит заголовком из localStorage, поэтому проверки
  // логина тут нет: без него endpoint ответит ошибкой и занятых просто не покажем
  useEffect(() => {
    if (groups.length === 0) {
      setBookedByRecord({});
      return;
    }
    const recordIds = groups.map((group) => group.id);
    serverFetch("OnlineClient/GroupRecordsBookedClients", { method: "GET", queryParams: { recordIds } })
      .then((data) => {
        const map = {};
        (data || []).forEach((item) => {
          map[item.recordId] = item.clientIds || [];
        });
        setBookedByRecord(map);
      })
      .catch((error) => {
        console.error("Ошибка получения занятых мест:", error);
        setBookedByRecord({});
      });
  }, [groups]);

  // Состав занятых мест нужен родителю: он показывает занятых участников
  // и блокирует повторную запись для выбранного занятия
  useEffect(() => {
    if (onBookedChange) {
      onBookedChange(bookedByRecord);
    }
  }, [bookedByRecord, onBookedChange]);

  const availableDates = useMemo(() => {
    const values = groups
      .filter((group) => group.docDate)
      .map((group) => dayjs(group.docDate).format("YYYY-MM-DD"));
    return [...new Set(values)];
  }, [groups]);

  const groupsBySelectedDate = useMemo(() => {
    if (!selectedDate) {
      return [];
    }
    return groups.filter((group) => group.docDate && dayjs(group.docDate).isSame(selectedDate, "day"));
  }, [groups, selectedDate]);

  const formatTime = (timeSpan) => {
    if (!timeSpan) {
      return "";
    }
    return timeSpan.length >= 5 ? timeSpan.substring(0, 5) : timeSpan;
  };

  const getGroupName = (group) => group.comment || group.productName || "Групповое занятие";

  /**
   * Подсказка о последних местах. Показываем её только пока место
   * действительно свободно для кого-то ещё: если занятость вместе с
   * выбранными нами участниками добивает лимит, последнее место уходит нам,
   * и подсказка вводит в заблуждение.
   */
  const getAvailabilityText = (group, projected) => {
    if (group.maxClients > 0 && projected >= group.maxClients) {
      return "";
    }
    if (group.availableCount === 1) {
      return "Осталось последнее место";
    }
    if (group.availableCount === 2) {
      return "Остались последние пару мест";
    }
    return "";
  };

  // Сколько мест занято будет, если записать всех выбранных участников.
  const getProjectedClientCount = (group, extra) => {
    const booked = Math.max(group.clientCount ?? 0, 0);
    return booked + Math.max(extra, 0);
  };

  // Занятость для карточки занятия. Свой вклад в неё показываем
  // только у выбранного занятия
  const getRecordProjectedCount = (group, bookedIds) => {
    const isSelected = group.id === selectedRecordId;
    // Уже записанные не занимают новое место: они уже в занятости занятия
    const extra = isSelected
      ? selectedClientIds.filter((clientId) => !bookedIds.includes(clientId)).length
      : 0;
    return getProjectedClientCount(group, extra);
  };

  // Записать в занятие больше некого: всех, кого мы собрались записать,
  // туда уже записали. Для клиента без группы это значит, что он сам
  // уже записан, даже если в занятии оставались свободные места.
  // Не зависит от выбора: занятое занятие видно сразу в списке.
   
  const isRecordClosedForMe = (bookedIds) => {
    // Никого не выбрали - значит ещё не решили, кого записывать
    if (selectedClientIds.length === 0) return false;
    // Закрыто, когда все выбранные уже в занятии: свободных мест хватит,
    // но записать там некого
    return selectedClientIds.every((clientId) => bookedIds.includes(clientId));
  };

  // Состояние занятости группы для окраски пилюли и полосы:
  // over - мест не хватает, warning - мест почти не осталось.
  // filled бывает только в расписании, в модалке его заменяет over.
  const getSlotsState = (group, projected) => {
    const remaining = group.maxClients - projected;
    if (remaining < 0) return "over";
    if (remaining === 0) return "filled";
    if (remaining <= SLOTS_WARNING_REMAINING) return "warning";
    return "";
  };

  // Получение текстовой метки для вида прохода по его значению
  const getPassVidLabel = (passVid) => {
    if (passVid == null) return null;
    const option = recordPassVidOptions.find(opt => opt.value === passVid);
    return option?.label || null;
  };

  const fetchGroups = useCallback(async (from, to) => {
    if (!company || !productId || !from || !to) {
      setGroups([]);
      return;
    }

    setLoading(true);
    if (!initialSelectedRecordId) {
      setSelectedRecordId(null);
      if (onSelect) {
        onSelect(null);
      }
    }

    try {
      const queryParams = {
        productId: productId,
        from: from.format("YYYY-MM-DD"),
        to: to.format("YYYY-MM-DD"),
      };
      const data = await serverFetch("Online/ProductGroups", { method: "GET", queryParams: queryParams }, user);
      const loadedGroups = data || [];
      const hasInitialGroup = initialGroupRecord && loadedGroups.some((group) => group.id === initialGroupRecord.id);
      const nextGroups = initialGroupRecord && !hasInitialGroup
        ? [initialGroupRecord, ...loadedGroups]
        : loadedGroups;
      const preselectedGroup = initialSelectedRecordId
        ? nextGroups.find((group) => group.id === initialSelectedRecordId)
        : null;
      setGroups(nextGroups);
      if (preselectedGroup) {
        setSelectedDate(dayjs(preselectedGroup.docDate));
        setSelectedRecordId(preselectedGroup.id);
      }
    } catch (error) {
      console.error("Ошибка получения групповых записей:", error);
      message.error(error?.UserMessage || "Не удалось загрузить групповые записи");
      setGroups(initialGroups);
    } finally {
      setLoading(false);
    }
  }, [company, initialGroupRecord, initialGroups, initialSelectedRecordId, onSelect, productId, user]);

  const handleDateSelect = useCallback(({ date }) => {
    setSelectedDate(date);
    const keepInitialSelection = initialSelectedRecordId && initialSelectedDate && date.isSame(initialSelectedDate, "day");
    if (!keepInitialSelection) {
      setSelectedRecordId(null);
      if (onSelect) {
        onSelect(null);
      }
    }
  }, [initialSelectedDate, initialSelectedRecordId, onSelect]);

  const handleGroupSelect = useCallback((group) => {
    setSelectedRecordId(group.id);
    if (onSelect) {
      onSelect(group);
    }
  }, [onSelect]);

  const openUserModal = (group) => {
    setModalUser({
      name: group.userName,
      imageId: group.userImageId,
      description: group.userDescription,
    });
    setIsModalOpen(true);
  };

  const closeUserModal = () => {
    setIsModalOpen(false);
    setModalUser(null);
  };

  return (
    <>
      <div className="catalog-sale-groups">
        <CatalogSaleDates
          onSelect={handleDateSelect}
          availableDates={availableDates}
          onlyAvailable={true}
          onWeekChange={fetchGroups}
          initialDate={initialSelectedDate}
        />
      {loading && groups.length === 0 ? (
        <div className="catalog-sale-groups-loading">
          <Spin size="small" />
        </div>
      ) : (
        <>
          {selectedDate && groupsBySelectedDate.length > 0 && (
            <div className="catalog-sale-groups-list-wrapper">
              <div className="catalog-sale-groups-title">Выберите группу</div>
              <div className="catalog-sale-groups-list">
                {groupsBySelectedDate.map((group) => {
                    // Мои участники, уже занявшие место в этом занятии
                    const bookedIds = bookedByRecord[group.id] || [];
                    // Места с учётом выбранных участников: занятые уже записанными
                    // нам места подставляем в счётчик сами
                    const projected = getRecordProjectedCount(group, bookedIds);
                    const closedForMe = isRecordClosedForMe(bookedIds);
                    return (
                    <div
                      key={group.id}
                      className={`catalog-sale-groups-card ${selectedRecordId === group.id ? "selected" : ""}${closedForMe ? " catalog-sale-groups-card_booked" : ""}`}
                      onClick={() => { if (!closedForMe) handleGroupSelect(group); }}
                      style={{ position: "relative" }}
                      aria-disabled={closedForMe}
                    >
                      {group.userImageId && (
                        <div className="catalog-sale-groups-photo">
                          <Image
                            src={`${process.env.REACT_APP_API_BASE_URL}/api/image/${group.userImageId}`}
                            alt={group.userName}
                            preview={false}
                            className="catalog-sale-groups-image"
                          />
                        </div>
                      )}
                      <div className="catalog-sale-groups-main">
                        <div className="catalog-sale-groups-name">{getGroupName(group)}</div>
                        {group.userDescription && (
                          <InfoCircleOutlined
                            onClick={(e) => {
                              e.stopPropagation();
                              openUserModal(group);
                            }}
                            style={{ position: "absolute", top: 8, right: 8, cursor: "pointer", color: "#8c8c8c" }}
                          />
                        )}
                        <div className="catalog-sale-groups-time">
                          {formatTime(group.start)} - {formatTime(group.finish)}
                        </div>
                        <div className="catalog-sale-groups-details">
                          {group.roomName && <span>{group.roomName}</span>}
                          {group.userName && <span>{group.userName}</span>}
                        </div>
                        {getPassVidLabel(group.passVid) && (
                          <div className="catalog-sale-groups-pass-hint" style={{ fontSize: 13, fontWeight: 1000, marginTop: 4 }}>
                            Запись производится {getPassVidLabel(group.passVid)}
                          </div>
                        )}
                        {group.maxClients > 0 && (() => {
                          const state = getSlotsState(group, projected);
                          // На сколько человек не хватает мест
                          const overflow = group.maxClients - projected;
                          const percent = Math.round((Math.min(projected, group.maxClients) / group.maxClients) * 100);
                          const isRed = state === "over" || state === "filled";
                          const pillState = isRed ? "over" : state;
                          const stateClass = pillState ? ` catalog-sale-groups-slots_${pillState}` : "";
                          return (
                            <div className={`catalog-sale-groups-slots${stateClass}`}>
                              <span className="catalog-sale-groups-slots-pill">
                                {closedForMe
                                  ? "Вы уже записаны"
                                  : state === "over"
                                    ? `Не хватает мест: ${Math.abs(overflow)}`
                                    : state === "filled"
                                      ? "Мест не осталось"
                                      : `Занято ${projected} из ${group.maxClients}`}
                              </span>
                              <span className="catalog-sale-groups-slots-bar" aria-hidden>
                                <span
                                  className="catalog-sale-groups-slots-bar-fill"
                                  style={{ width: `${Math.max(2, Math.min(100, percent))}%` }}
                                />
                              </span>
                            </div>
                          );
                        })()}
                        {getAvailabilityText(group, projected) && (
                          <div className="catalog-sale-groups-availability">
                            {getAvailabilityText(group, projected)}
                          </div>
                        )}
                      </div>
                    </div>
                    );
                })}
              </div>
            </div>
          )}
          {selectedDate && groupsBySelectedDate.length === 0 && (
            <div className="catalog-sale-groups-empty">На выбранную дату нет доступных групп</div>
          )}
        </>
      )}
    </div>
    <Modal
      title="Информация о сотруднике"
      open={isModalOpen}
      onCancel={closeUserModal}
      footer={null}
      centered
      width={700}
    >
      {modalUser && (
        <div className="trainer-modal-content">
          {modalUser.imageId && (
            <div className="trainer-modal-photo-container">
              <img
                src={`${process.env.REACT_APP_API_BASE_URL}/api/image/${modalUser.imageId}`}
                alt={modalUser.name}
                className="trainer-modal-photo"
              />
            </div>
          )}
          <div className="trainer-modal-info">
            <div className="trainer-modal-name">{modalUser.name}</div>
            <div className="trainer-modal-description">
              {modalUser.description}
            </div>
          </div>
        </div>
      )}
    </Modal>
  </>);
};

export default CatalogSaleGroups;